import { useMutation } from "@apollo/client";
import { Clipboard } from "@capacitor/clipboard";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { produce } from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import ActionIconButton from "@components/buttons/ActionIconButton";
import BaseErrors from "@components/fields/BaseErrors";
import DataSelect from "@components/fields/DataSelect";
import DateInput from "@components/fields/DateInput";
import Input from "@components/fields/Input";
import LocationAutocompleteInput from "@components/fields/LocationAutocompleteInput";
import Select from "@components/fields/Select";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import Form from "@components/forms/Form";
import {
  CareProfileAcuityEnum,
  CareProfileAgeWithDeprecatedEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileImmigrationStatusEnum,
  CareProfileMaritalStatusEnum,
  CareProfileTypesOfSupportNeededEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  ClientRoleRelationshipEnum,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery,
  PronounsEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import {
  optionsForAge,
  optionsForMaritalStatus,
  optionsForPronouns,
  optionsForTypesOfSupportNeeded
} from "@utils/optionUtils";
import { dateToMDYString } from "@utils/timeUtils";

type FormData = {
  acuity?: CareProfileAcuityEnum | null;
  age?: CareProfileAgeWithDeprecatedEnum | null;
  culturalConsiderations?: string | null;
  dateOfBirth?: string | null;
  dateOfDeath?: string | null;
  guardianMaritalStatus?: CareProfileMaritalStatusEnum | null;
  immigrationStatus?: CareProfileImmigrationStatusEnum | null;
  languageCode?: string | null;
  maritalStatus?: CareProfileMaritalStatusEnum | null;
  name?: string | null;
  placeId?: string | null;
  pregnancyDueDate?: string | null;
  pronouns?: PronounsEnum | null;
  pronounsOther?: string | null;
  typesOfSupportNeeded?: CareProfileTypesOfSupportNeededEnum[];
};

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

const CareProfileDemographicsForm = ({ careProfileFieldRules, journey }: Props) => {
  const intl = useIntl();

  const careProfile = journey.careProfile;

  let defaultDateOfBirth = null;
  if (careProfile.dateOfBirth) {
    defaultDateOfBirth = dateToMDYString(careProfile.dateOfBirth);
  }

  let defaultDateOfDeath = null;
  if (careProfile.dateOfDeath) {
    defaultDateOfDeath = dateToMDYString(careProfile.dateOfDeath);
  }

  let defaultPregnancyDueDate = null;
  if (careProfile.pregnancyDueDate) {
    defaultPregnancyDueDate = dateToMDYString(careProfile.pregnancyDueDate);
  }

  const defaultValues: FormData = {
    acuity: careProfile.acuity ?? null,
    age: careProfile.age ?? null,
    culturalConsiderations: careProfile.culturalConsiderations ?? null,
    dateOfBirth: defaultDateOfBirth,
    dateOfDeath: defaultDateOfDeath,
    guardianMaritalStatus: careProfile.guardianMaritalStatus ?? null,
    immigrationStatus: careProfile.immigrationStatus ?? null,
    languageCode: careProfile.languageCode ?? null,
    maritalStatus: careProfile.maritalStatus ?? null,
    name: careProfile.name ?? null,
    placeId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS
      ? "ChIJfX6-HwSbf4gR4Aa9pkeWBLY"
      : careProfile.location.placeId,
    pregnancyDueDate: defaultPregnancyDueDate,
    pronouns: careProfile.pronouns,
    pronounsOther: careProfile.pronounsOther,
    typesOfSupportNeeded: careProfile.typesOfSupportNeeded
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      acuity: yup.string().nullable().oneOf(Object.values(CareProfileAcuityEnum)),
      age: yup.string().nullable().oneOf(Object.values(CareProfileAgeWithDeprecatedEnum)),
      culturalConsiderations: yup.string().nullable(),
      dateOfBirth: yup.string().nullable(),
      dateOfDeath: yup.string().nullable(),
      guardianMaritalStatus: yup.string().nullable().oneOf(Object.values(CareProfileMaritalStatusEnum)),
      immigrationStatus: yup.string().nullable().oneOf(Object.values(CareProfileImmigrationStatusEnum)),
      languageCode: yup.string().nullable(),
      maritalStatus: yup.string().nullable().oneOf(Object.values(CareProfileMaritalStatusEnum)),
      name: yup.string().nullable(),
      placeId: yup.string().nullable(),
      pregnancyDueDate: yup.string().nullable(),
      pronouns: yup.string().nullable().oneOf(Object.values(PronounsEnum)),
      pronounsOther: yup.string().nullable(),
      typesOfSupportNeeded: yup
        .array()
        .of(yup.string().oneOf(Object.values(CareProfileTypesOfSupportNeededEnum)).defined())
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    const valuesWithoutErrors = produce(form.getValues(), draftValues => {
      Object.keys(form.formState.errors).forEach(field => {
        delete draftValues[field as keyof FormData];
      });
    });

    setBaseErrors([]);
    return updateCareProfile({
      variables: {
        ...valuesWithoutErrors,
        id: journey.careProfile.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.careProfile"
      });
    });
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  const notDebouncedFields = useMemo(() => ["acuity", "age"], []);

  useEffect(() => {
    const debouncedSubmit = debounce(submitCareProfile, 2000);
    const subscription = form.watch((_, { name }) => {
      if (name && notDebouncedFields.includes(name)) {
        form.handleSubmit(submitCareProfile, submitCareProfile)();
      } else {
        form.handleSubmit(debouncedSubmit, submitCareProfile)();
      }
    });
    return () => subscription.unsubscribe();
  }, [form, notDebouncedFields, submitCareProfile]);

  const handleCopyLocationToClipboard = () => {
    if (careProfile.location.address) {
      Clipboard.write({ string: careProfile.location.address });
    }
  };

  const ageOptions = optionsForAge(intl);
  const maritalStatusOptions = optionsForMaritalStatus(intl);
  const pronounsOptions = optionsForPronouns(intl);
  const typesOfSupportNeededOptions = optionsForTypesOfSupportNeeded(intl);

  return (
    <Form hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.formSectionRow}>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={journey.ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.myself}
            fieldName={CareProfileFieldNameEnum.name}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="name"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Input<FormData>
              control={form.control}
              error={form.formState.errors.name}
              label={intl.formatMessage({ id: "forms.careProfileNew.nameLabel" })}
              name="name"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={journey.ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.myself}
            fieldName={CareProfileFieldNameEnum.pronouns}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pronouns"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.pronouns}
              label={intl.formatMessage({ id: "forms.careProfileNew.pronounsLabel" })}
              name="pronouns"
              options={pronounsOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={journey.ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.myself}
            fieldName={CareProfileFieldNameEnum.pronouns_other}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pronounsOther"
            otherField="pronouns"
            otherValue={PronounsEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Input<FormData>
              control={form.control}
              error={form.formState.errors.pronounsOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.pronounsOtherLabel" })}
              name="pronounsOther"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.age}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="age"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.age}
              label={intl.formatMessage({ id: "forms.careProfileNew.ageLabel" })}
              name="age"
              options={ageOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.date_of_birth}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="dateOfBirth"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <DateInput<FormData>
              control={form.control}
              error={form.formState.errors.dateOfBirth}
              label={intl.formatMessage({ id: "forms.careProfileNew.dateOfBirthLabel" })}
              name="dateOfBirth"
              pastOnly
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.date_of_death}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="dateOfDeath"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <DateInput<FormData>
              control={form.control}
              error={form.formState.errors.dateOfDeath}
              label={intl.formatMessage({ id: "forms.careProfileNew.dateOfDeathLabel" })}
              name="dateOfDeath"
              pastOnly
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.pregnancy_due_date}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pregnancyDueDate"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <DateInput<FormData>
              control={form.control}
              error={form.formState.errors.pregnancyDueDate}
              label={intl.formatMessage({ id: "forms.careProfileNew.pregnancyDueDateLabel" })}
              name="pregnancyDueDate"
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <div className={styles.selectWithCopyButton}>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.place_id}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="placeId"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <LocationAutocompleteInput
                control={form.control}
                error={form.formState.errors.placeId}
                label={intl.formatMessage({ id: "forms.careProfileNew.placeIdLabel" })}
                location={{ address: careProfile.location.address, placeId: careProfile.location.placeId }}
                name="placeId"
              />
            </CareProfileFormCondition>
            {careProfile.location.address && (
              <div className={styles.copyButton}>
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.copy"
                  icon="clone"
                  onClick={handleCopyLocationToClipboard}
                />
              </div>
            )}
          </div>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.language_code}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="languageCode"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <DataSelect<FormData>
              control={form.control}
              error={form.formState.errors.languageCode}
              hasNoneOption
              label={intl.formatMessage({ id: "forms.careProfileNew.languageCodeLabel" })}
              name="languageCode"
              type="languages"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.marital_status}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="maritalStatus"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.maritalStatus}
              label={intl.formatMessage({ id: "forms.careProfileNew.maritalStatusLabel" })}
              name="maritalStatus"
              options={maritalStatusOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.guardian_marital_status}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="guardianMaritalStatus"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.guardianMaritalStatus}
              label={intl.formatMessage({ id: "forms.careProfileNew.guardianMaritalStatusLabel" })}
              name="guardianMaritalStatus"
              options={maritalStatusOptions}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.cultural_considerations}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="culturalConsiderations"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Input<FormData>
              control={form.control}
              error={form.formState.errors.culturalConsiderations}
              label={intl.formatMessage({
                id: "forms.careProfileNew.culturalConsiderationsLabel"
              })}
              name="culturalConsiderations"
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.types_of_support_needed}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="typesOfSupportNeeded"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.typesOfSupportNeeded}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.typesOfSupportNeededLabel" })}
              name="typesOfSupportNeeded"
              options={typesOfSupportNeededOptions}
            />
          </CareProfileFormCondition>
        </div>
      </div>
    </Form>
  );
};

export default CareProfileDemographicsForm;
